import * as Sentry from '@sentry/react';
import { gql } from '@apollo/client';

const BIN_SETS_QUERY = gql`
  query DocumentFactory_BinSetsQuery($farmId: uuid!) {
    farm(where: { id: { _eq: $farmId }, deleted_at: { _is_null: true } }) {
      id
      name
      bin_sets(
        where: { deleted_at: { _is_null: true } }
        order_by: [{ bins_aggregate: { min: { name: asc } } }, { created_at: desc }]
      ) {
        id
        bins(where: { deleted_at: { _is_null: true } }) {
          id
          name
        }
      }
    }
  }
`;

const BIN_SET_LEVEL_HISTORY_QUERY = gql`
  query DocumentFactory_BinSetLevelHistoryQuery(
    $bin_set_id: uuid!
    $start_time: bigint
    $end_time: bigint
    $algorithm_version: String
    $timezone: String
  ) {
    bin_set_level_history(
      bin_set_id: $bin_set_id
      start_time: $start_time
      end_time: $end_time
      algorithm_version: $algorithm_version
      timezone: $timezone
    ) {
      bin_set_id
      bin_set_history
    }
  }
`;

class BinSetLevelPredictionsFactory {
  constructor({
    farmId,
    gqlClient,
    strategy,
    progressCallback,
    isMetric,
    startTime,
    endTime,
    algorithmVersion,
    timezone,
  }) {
    this.strategy = strategy;
    this.progressCallback = progressCallback;
    this.farmId = farmId;
    this.binSets = [];
    this.gqlClient = gqlClient;
    this.isMetric = isMetric;
    this.startTime = startTime;
    this.endTime = endTime;
    this.algorithmVersion = algorithmVersion;
    this.timezone = timezone;
  }

  async start() {
    if (!this.strategy) {
      Sentry?.captureMessage('BinSetLevelPredictionsFactory does not have a strategy provided');
      return;
    }

    this.progressCallback(0);

    try {
      // First, fetch bin set IDs
      await this.fetchBinSets();

      this.progressCallback(0.1);

      // Set bin set objects in strategy
      this.strategy.setBinSetObjects(this.binSets);

      this.progressCallback(0.2);

      // Download data for each bin set individually
      await this.downloadBinSetLevelHistory();

      this.progressCallback(1);
    } catch (error) {
      console.error('Error in BinSetLevelPredictionsFactory:', error);
      Sentry?.captureException(error);
      this.progressCallback(0);
      throw error;
    }
  }

  async fetchBinSets() {
    const response = await this.gqlClient.query({
      query: BIN_SETS_QUERY,
      variables: {
        farmId: this.farmId,
      },
      fetchPolicy: 'network-only',
    });

    const farm = response.data.farm?.[0];
    if (farm && farm.bin_sets) {
      this.binSets = farm.bin_sets;
    } else {
      throw new Error('No bin sets found for this farm');
    }
  }

  async downloadBinSetLevelHistory() {
    const totalBinSets = this.binSets.length;
    let processedBinSets = 0;

    for (const binSet of this.binSets) {
      try {
        // Download data for this bin set
        const binSetData = await this.getBinSetLevelHistory(binSet.id);

        // Process the data with the strategy
        if (this.strategy.processBinSetLevelHistory) {
          this.strategy.processBinSetLevelHistory(binSet.id, binSetData);
        }

        processedBinSets++;
        this.progressCallback(0.2 + 0.8 * (processedBinSets / totalBinSets));
      } catch (error) {
        console.error(`Error downloading data for bin set ${binSet.id}:`, error);
        Sentry?.captureException(error);
        throw error;
      }
    }
  }

  async getBinSetLevelHistory(binSetId) {
    const response = await this.gqlClient.query({
      query: BIN_SET_LEVEL_HISTORY_QUERY,
      variables: {
        bin_set_id: binSetId,
        start_time: this.startTime,
        end_time: this.endTime,
        ...(this.algorithmVersion ? { algorithm_version: this.algorithmVersion } : {}),
        timezone: this.timezone,
      },
      fetchPolicy: 'network-only',
    });

    return response.data.bin_set_level_history;
  }

  createFile() {
    return this.strategy?.createFile();
  }
}

export default BinSetLevelPredictionsFactory;
